/**
 * Reubicar Miembros entre colecciones
 *
 * Mueve 10 miembros de asesor → asociado según el CSV oficial.
 * Actualiza referencias en propiedades (asesor_doc_ids).
 */

import React, { useState } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface MoveEntry {
  nombre: string;
  correo: string;
  csvTipo: string;
  fromCol: 'asesor' | 'asociado';
  toCol: 'asesor' | 'asociado';
  docId: string;
}

const MOVES: MoveEntry[] = [];

type ItemStatus = 'pending' | 'processing' | 'done' | 'error';

export default function ReubicarMiembros() {
  const { user } = useUser();
  const [moves, setMoves] = useState<MoveEntry[]>(MOVES);
  const [statuses, setStatuses] = useState<Map<number, { status: ItemStatus; msg?: string; newDocId?: string }>>(new Map());
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);

  const addLog = (msg: string) => setLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);

  // Search for Marco Torres in asociado collection and add to moves
  const buscarMarco = async () => {
    setSearching(true);
    addLog('Buscando Marco Esteban Torres Andrade en colección asociado...');
    try {
      const snap = await getDocs(collection(db, 'asociado'));
      let found = false;
      snap.forEach(d => {
        const data = d.data();
        const nombre = (data.nombre || '').toLowerCase();
        if (nombre.includes('marco') && nombre.includes('torres')) {
          addLog(`Encontrado: ${data.nombre} (${d.id}) - correo: ${data.correo || 'sin correo'}`);
          setMoves([{
            nombre: data.nombre,
            correo: data.correo || 'marco@mexpatrealtors.com',
            csvTipo: 'AFILIADO',
            fromCol: 'asociado',
            toCol: 'asesor',
            docId: d.id,
          }]);
          found = true;
        }
      });
      if (!found) addLog('No se encontró a Marco Torres en asociado');
    } catch (err: any) {
      addLog(`Error: ${err.message}`);
    }
    setSearching(false);
  };

  const ejecutar = async () => {
    setRunning(true);
    let moved = 0;
    let propsUpdated = 0;

    addLog(`Reubicando ${moves.length} miembros de asesor → asociado...`);

    for (let i = 0; i < moves.length; i++) {
      const entry = moves[i];
      setStatuses(prev => new Map(prev).set(i, { status: 'processing' }));

      try {
        // 1. Read original document
        const oldDocRef = doc(db, entry.fromCol, entry.docId);
        const oldSnap = await getDoc(oldDocRef);

        if (!oldSnap.exists()) {
          setStatuses(prev => new Map(prev).set(i, { status: 'error', msg: 'Doc no encontrado' }));
          addLog(`Error: ${entry.nombre} - documento ${entry.docId} no existe en ${entry.fromCol}`);
          continue;
        }

        const data = oldSnap.data();

        // 2. Create new document in target collection
        const newData: any = {
          ...data,
          tipo: entry.toCol === 'asociado' ? 'asociado' : 'asesor',
          fecha_actualizacion: serverTimestamp(),
        };

        // Adjust field names for collection change
        if (entry.toCol === 'asociado') {
          // Moving asesor → asociado
          // asociado uses inmobiliaria_doc_id (single), asesor uses inmobiliaria_doc_ids (array)
          if (Array.isArray(data.inmobiliaria_doc_ids) && data.inmobiliaria_doc_ids.length > 0) {
            newData.inmobiliaria_doc_id = data.inmobiliaria_doc_ids[0];
          }
          delete newData.inmobiliaria_doc_ids;

          // Rename ID field
          if (data.asesor_id) {
            newData.asociado_id = data.asesor_id;
            delete newData.asesor_id;
          }
        } else {
          // Moving asociado → asesor
          if (data.inmobiliaria_doc_id) {
            newData.inmobiliaria_doc_ids = [data.inmobiliaria_doc_id];
          }
          delete newData.inmobiliaria_doc_id;

          if (data.asociado_id) {
            newData.asesor_id = data.asociado_id;
            delete newData.asociado_id;
          }

          if (!newData.propiedad_id) {
            newData.propiedad_id = [];
          }
        }

        const newDocRef = await addDoc(collection(db, entry.toCol), newData);
        const newDocId = newDocRef.id;

        addLog(`${entry.nombre}: creado en ${entry.toCol} (${newDocId})`);

        // 3. Update property references (asesor_doc_ids: replace old docId with new)
        const propSnap = await getDocs(
          query(collection(db, 'propiedades'), where('asesor_doc_ids', 'array-contains', entry.docId))
        );

        if (propSnap.size > 0) {
          const batch = writeBatch(db);
          propSnap.forEach(propDoc => {
            const propData = propDoc.data();
            const oldIds: string[] = propData.asesor_doc_ids || [];
            const newIds = oldIds.map((id: string) => id === entry.docId ? newDocId : id);
            batch.update(doc(db, 'propiedades', propDoc.id), { asesor_doc_ids: newIds });
          });
          await batch.commit();
          propsUpdated += propSnap.size;
          addLog(`  → ${propSnap.size} propiedades actualizadas`);
        }

        // 4. Delete old document
        await deleteDoc(oldDocRef);
        addLog(`  → Eliminado de ${entry.fromCol}`);

        moved++;
        setStatuses(prev => new Map(prev).set(i, { status: 'done', newDocId }));
      } catch (err: any) {
        addLog(`Error ${entry.nombre}: ${err.message}`);
        setStatuses(prev => new Map(prev).set(i, { status: 'error', msg: err.message }));
      }
    }

    addLog(`Completado: ${moved} miembros reubicados, ${propsUpdated} propiedades actualizadas`);
    setRunning(false);
  };

  const doneCount = Array.from(statuses.values()).filter(s => s.status === 'done').length;

  const isAdmin = user?.rol === 'ADMIN' || user?.tipo?.includes?.('admin');
  if (!isAdmin) return <div className="container py-5"><h3>Acceso no autorizado</h3></div>;

  return (
    <>
      <UserNav />
      <Breadcrumb items={[
        { label: 'Admin', path: '/admin-dashboard' },
        { label: 'Reubicar Miembros' }
      ]} />

      <section className="py-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11">

              <h4 className="mb-3">
                <i className="fa-solid fa-arrow-right-arrow-left me-2 text-warning"></i>
                Reubicar Miembros entre colecciones
              </h4>

              <div className="alert alert-info">
                <strong>Mueve miembros de una colección a otra según el CSV oficial.</strong>
                <ul className="mb-0 mt-2">
                  <li>Se actualizarán las referencias en propiedades (<code>asesor_doc_ids</code>)</li>
                  <li>Se eliminará el documento original</li>
                  <li>Campos se adaptan automáticamente entre colecciones</li>
                </ul>
              </div>

              {moves.length === 0 && (
                <div className="card border-warning mb-4">
                  <div className="card-body py-3 d-flex align-items-center justify-content-between">
                    <div>
                      <strong>Marco Esteban Torres Andrade</strong>
                      <small className="d-block text-muted">CSV dice AFILIADO, actualmente en colección asociado → mover a asesor</small>
                    </div>
                    <button className="btn btn-warning" onClick={buscarMarco} disabled={searching}>
                      {searching ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span>Buscando...</>
                      ) : (
                        <><i className="fa-solid fa-search me-2"></i>Buscar y preparar</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <button
                className="btn btn-warning btn-lg mb-4"
                onClick={ejecutar}
                disabled={running || doneCount > 0}
              >
                {running ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>Procesando...</>
                ) : doneCount > 0 ? (
                  <><i className="fa-solid fa-check me-2"></i>Completado ({doneCount}/{moves.length})</>
                ) : (
                  <><i className="fa-solid fa-play me-2"></i>Ejecutar Reubicación</>
                )}
              </button>

              <div className="table-responsive">
                <table aria-label="Tabla de Reubicar Miembros" className="table table-sm table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Nombre</th>
                      <th scope="col">Correo</th>
                      <th scope="col">De</th>
                      <th scope="col">A</th>
                      <th scope="col">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moves.map((m, i) => {
                      const s = statuses.get(i);
                      return (
                        <tr key={i} className={s?.status === 'done' ? 'table-success' : s?.status === 'error' ? 'table-danger' : ''}>
                          <td>{i + 1}</td>
                          <td><strong>{m.nombre}</strong></td>
                          <td><small>{m.correo}</small></td>
                          <td><span className="badge bg-info">asesor (Afiliado)</span></td>
                          <td><span className="badge bg-secondary">asociado (Asociado)</span></td>
                          <td>
                            {s?.status === 'processing' && <span className="spinner-border spinner-border-sm text-primary"></span>}
                            {s?.status === 'done' && <span className="badge bg-success"><i className="fa-solid fa-check me-1"></i>Movido</span>}
                            {s?.status === 'error' && <span className="badge bg-danger" title={s.msg}>Error</span>}
                            {!s && <span className="text-muted">Pendiente</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {log.length > 0 && (
                <div className="card mt-4 border-0 bg-dark text-light">
                  <div className="card-header bg-dark border-bottom border-secondary">
                    <small className="text-muted">Log</small>
                  </div>
                  <div className="card-body p-3" style={{ maxHeight: '250px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {log.map((msg, i) => <div key={i}>{msg}</div>)}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="" />
      <Footer />
    </>
  );
}
